import { ChevronLeft, ChevronRight, MoreHorizontal } from 'lucide-react';
import { joinCss, pickCss } from '@/shared/lib/css-utils';
import css from './day-picker-bar.module.scss';

const styles = {
  root: pickCss(css, css.root),
  nav: pickCss(css, css.nav),
  field: pickCss(css, css.field),
  titles: pickCss(css, css.titles),
  title: pickCss(css, css.title),
  metaRow: pickCss(css, css.metaRow),
  dot: pickCss(css, css.dot),
  meta: pickCss(css, css.meta),
  chevron: pickCss(css, css.chevron),
  overflow: pickCss(css, css.overflow),
};

type DayPickerBarProps = {
  dayLabel: string;
  dayMeta: string;
  /** 'attention' shows the amber status dot, 'ok' shows green. */
  tone: 'ok' | 'attention';
  onPrev: () => void;
  onNext: () => void;
  onOpenMenu: () => void;
  /**
   * Optional handler for the trailing `⋯` button. Pen `J4NoGu` always
   * renders the button; without a handler it stays inert (the design's
   * empty-state). Wire from the parent when there are per-day actions.
   */
  onOverflow?: () => void;
  prevDisabled?: boolean;
  nextDisabled?: boolean;
};

/**
 * DayPickerBar — pen component `J4NoGu`. The day navigation strip at the top
 * of Step 2: prev / next arrows around a "Day N · City" field that opens the
 * `DayPickerMenu` jump list.
 */
export const DayPickerBar = ({
  dayLabel,
  dayMeta,
  tone,
  onPrev,
  onNext,
  onOpenMenu,
  onOverflow,
  prevDisabled,
  nextDisabled,
}: DayPickerBarProps) => (
  <div className={styles.root()}>
    <button
      aria-label="Previous day"
      className={styles.nav()}
      disabled={prevDisabled}
      onClick={onPrev}
      type="button"
    >
      <ChevronLeft aria-hidden="true" size={18} />
    </button>
    <button
      aria-haspopup="menu"
      className={styles.field()}
      onClick={onOpenMenu}
      type="button"
    >
      <span className={styles.titles()}>
        <span className={styles.title()}>{dayLabel}</span>
        <span className={styles.metaRow()}>
          <span aria-hidden="true" className={joinCss(styles.dot({ tone }))} />
          <span className={joinCss(styles.meta({ tone }))}>{dayMeta}</span>
        </span>
      </span>
      <span aria-hidden="true" className={styles.chevron()}>
        ▾
      </span>
    </button>
    <button
      aria-label="Next day"
      className={styles.nav()}
      disabled={nextDisabled}
      onClick={onNext}
      type="button"
    >
      <ChevronRight aria-hidden="true" size={18} />
    </button>
    <button
      aria-label="Day options"
      className={styles.overflow()}
      disabled={!onOverflow}
      onClick={onOverflow}
      type="button"
    >
      <MoreHorizontal aria-hidden="true" size={18} />
    </button>
  </div>
);
